import Icon from 'components/Icon';
import * as consts from 'consts/consts';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Col } from 'reactstrap';
import { Person } from 'types';
import {
  formatTimezoneName,
  getUtcInstantFromWallClock,
} from 'utils/util/timezone';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import FilterablePeopleTable, {
  FilterType,
} from 'views/Widgets/People/FilterablePeopleTable';
import AddScheduledParticipantsModal from './AddScheduledParticipantsModal';
import { type ScheduledParticipant } from './consts';
import EditScheduledParticipantModal from './EditScheduledParticipantModal';

interface Props {
  value?: ScheduledParticipant[] | null;
  onChange: (participants: ScheduledParticipant[]) => void;
  disabled?: boolean;
  targetFilter?: Record<string, unknown> | null;
  // Off while creating a survey: nothing can have been sent before it exists,
  // so the column would read "No" for every row.
  showSentFilter?: boolean;
}

const ScheduledParticipantsField: FC<Props> = ({
  value,
  onChange,
  disabled = false,
  targetFilter = null,
  showSentFilter = false,
}) => {
  const { formatMessage, locale } = useIntl();
  const [isAddPeopleModalOpen, setIsAddPeopleModalOpen] = useState(false);
  // Positions rather than rows: the same person can hold several rows, so
  // nothing on a row identifies which one is being edited or removed.
  const [participantIndexPendingRemoval, setParticipantIndexPendingRemoval] =
    useState<number | null>(null);
  const [participantIndexBeingEdited, setParticipantIndexBeingEdited] =
    useState<number | null>(null);

  const participants = useMemo(() => value ?? [], [value]);

  const participantPendingRemoval =
    participantIndexPendingRemoval === null
      ? null
      : participants[participantIndexPendingRemoval];
  const participantBeingEdited =
    participantIndexBeingEdited === null
      ? null
      : participants[participantIndexBeingEdited];

  const toggleAddPeopleModal = useCallback(() => {
    setIsAddPeopleModalOpen((prev) => !prev);
  }, []);

  // Deliberately doesn't close the modal: ModalEditor calls toggle() itself
  // right after this, so closing here would toggle it straight back open.
  const handleAddParticipants = useCallback(
    (added: ScheduledParticipant[]) => {
      if (added.length > 0) {
        onChange([...participants, ...added]);
      }
    },
    [participants, onChange]
  );

  const closeRemoveConfirmation = useCallback(
    () => setParticipantIndexPendingRemoval(null),
    []
  );

  const closeEditModal = useCallback(
    () => setParticipantIndexBeingEdited(null),
    []
  );

  const handleEditParticipant = useCallback(
    (updated: ScheduledParticipant | null) => {
      if (updated && participantIndexBeingEdited !== null) {
        onChange(
          participants.map((p, participantIndex) =>
            participantIndex === participantIndexBeingEdited ? updated : p
          )
        );
      }
      setParticipantIndexBeingEdited(null);
    },
    [participants, participantIndexBeingEdited, onChange]
  );

  const handleRemoveParticipant = useCallback(() => {
    if (participantIndexPendingRemoval !== null) {
      onChange(
        participants.filter(
          (_, participantIndex) =>
            participantIndex !== participantIndexPendingRemoval
        )
      );
    }
    setParticipantIndexPendingRemoval(null);
  }, [participants, participantIndexPendingRemoval, onChange]);

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.column.person',
          defaultMessage: 'Person',
        }),
        field: 'person',
        isPerson: true,
        sortable: false,
        getFilterDisplayValue: (person?: Person | null) =>
          person?.full_name ?? '',
        // The table's default `person` transformer matches on the row's own
        // id, which here is the participant's position, not a person.
        fieldTransformer: (value, operator) =>
          `(${operator == 'EQ' ? '==' : '!='} "${
            value.filterObject.value
          }" person.id)`,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.column.when',
          defaultMessage: 'Sending',
        }),
        field: 'when',
        // Sorted on the raw instant, not the rendered cell: "April" sorts
        // before "January" as a string.
        sort: 'when_sort',
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.column.timezone',
          defaultMessage: 'Timezone',
        }),
        field: 'timezone',
        sortable: false,
      },
      ...(showSentFilter
        ? [
            {
              name: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.column.sent',
                defaultMessage: 'Sent',
              }),
              field: 'sent',
            },
          ]
        : []),
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.column.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'text-end text-nowrap',
        sortable: false,
        // The only column kept out of the filter builder: a cell of buttons
        // has no value to filter on.
        hideFromFilters: true,
      },
    ],
    [formatMessage, showSentFilter]
  );

  const rows = useMemo(
    () =>
      participants.map((participant, participantIndex) => ({
        // The row's position, since a person may appear on several rows.
        id: participantIndex,
        person: participant.person,
        // Not getPrettyDate's includeTime: it appends the viewer's timezone,
        // which would contradict the row's own zone in the next column.
        when: new Date(participant.scheduled_time).toLocaleString(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }),
        // The real instant, not the digits on screen: what the editor holds is
        // a wall clock, so noon in New York and noon in London would otherwise
        // sort as the same moment.
        when_sort: getUtcInstantFromWallClock(
          new Date(participant.scheduled_time),
          participant.scheduled_timezone
        ),
        timezone: formatTimezoneName(participant.scheduled_timezone),
        // A plain string, not a boolean: the filter input builds its
        // suggestions from the rendered cell values.
        sent: participant.sent_at
          ? formatMessage({
              id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.sent_yes',
              defaultMessage: 'Yes',
            })
          : formatMessage({
              id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.sent_no',
              defaultMessage: 'No',
            }),
        // Nothing to offer on a sent row: it can't be retimed or removed, and
        // the Sent column already says so.
        actions: participant.sent_at ? null : (
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button
              type="button"
              color="link"
              className="p-0"
              disabled={disabled}
              onClick={() => setParticipantIndexBeingEdited(participantIndex)}
              aria-label={formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.edit',
                defaultMessage: 'Change send time',
              })}
            >
              <Icon icon={consts.ICONS.EDIT} />
            </Button>
            <Button
              type="button"
              color="link"
              className="p-0 text-danger"
              disabled={disabled}
              onClick={() =>
                setParticipantIndexPendingRemoval(participantIndex)
              }
              aria-label={formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.remove',
                defaultMessage: 'Remove person',
              })}
            >
              <Icon icon={consts.ICONS.TRASH} />
            </Button>
          </div>
        ),
      })),
    [participants, locale, disabled, formatMessage]
  );

  // A Col, not a bare Button: the table drops `actions` straight into its
  // header Row, so it has to carry its own column sizing to sit beside the
  // filter toggle rather than stretch across it.
  const addButton = useMemo(
    () => (
      <Col className="col-auto ps-0 ms-auto">
        <Button
          type="button"
          color="primary"
          size="sm"
          onClick={toggleAddPeopleModal}
          disabled={disabled}
        >
          <FormattedMessage
            id="app.views.administration.custom_surveys.surveys.scheduled_participants.add"
            defaultMessage="Add people"
          />
        </Button>
      </Col>
    ),
    [toggleAddPeopleModal, disabled]
  );

  return (
    <>
      <FilterablePeopleTable
        title={formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.label',
          defaultMessage: 'People to survey',
        })}
        columns={columns}
        rows={rows}
        actions={addButton}
        hideFilters={!showSentFilter}
        filterType={FilterType.ADVANCED}
        hideExportButton={true}
      />

      {/* The modal portals to document.body, but React events bubble through
          the React tree - without this its submit would reach the survey form
          this field sits inside and save the whole survey. */}
      <div onSubmit={(event) => event.stopPropagation()}>
        <AddScheduledParticipantsModal
          isOpen={isAddPeopleModalOpen}
          toggle={toggleAddPeopleModal}
          onAdd={handleAddParticipants}
          targetFilter={targetFilter}
        />

        <EditScheduledParticipantModal
          isOpen={!!participantBeingEdited}
          toggle={closeEditModal}
          participant={participantBeingEdited}
          onSave={handleEditParticipant}
        />
      </div>

      {participantPendingRemoval && (
        <ConfirmationDialogModal
          isOpen={!!participantPendingRemoval}
          onClosed={closeRemoveConfirmation}
          toggle={closeRemoveConfirmation}
          confirmCallback={handleRemoveParticipant}
          title={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.remove.title',
            defaultMessage: 'Remove person',
          })}
          description={formatMessage(
            {
              id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.remove.description',
              defaultMessage:
                '{name} will no longer receive this survey once you save.',
            },
            { name: participantPendingRemoval.person?.full_name ?? '' }
          )}
          confirmText={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.scheduled_participants.remove.confirm',
            defaultMessage: 'Remove',
          })}
        />
      )}
    </>
  );
};

export default React.memo(ScheduledParticipantsField);
